import { AfterViewInit, Component, ElementRef, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { DeviceSelectorComponent } from '../../../device-selector.component';

type PhotoRecorderState = 'previewing' | 'reviewing';

@Component({
  selector: 'app-photo-recorder-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressBarModule, TranslateModule, DeviceSelectorComponent],
  template: `
    <div class="standard-dialog">
      <div class="header" mat-dialog-title>
        <div class="title-section">
          <h2>{{ (recorderState() === 'reviewing' ? 'PhotoRecorder.Review.Title' : 'PhotoRecorder.Title') | translate }}</h2>
          <p class="subtitle">
            {{ (recorderState() === 'reviewing' ? 'PhotoRecorder.Review.Subtitle' : 'PhotoRecorder.Subtitle') | translate }}
          </p>
        </div>
        <button mat-icon-button (click)="closeDialog()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="form-content">
        @if (recorderState() !== 'reviewing') {
          <div class="device-selectors-container">
            <app-device-selector
              [kind]="'videoinput'"
              [label]="'DeviceSelector.CameraLabel'"
              (selectionChange)="onCameraSelected($event)"
              [isDisabled]="recorderState() === 'reviewing'"
            ></app-device-selector>
          </div>
        }

        @if (isLoading()) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }

        <div class="video-container rounded-lg overflow-hidden bg-black flex items-center justify-center" [class.vertical-camera]="isVerticalCamera()">
          @if (recorderState() === 'previewing') {
            <video #livePreview class="video-element" autoplay playsinline muted (loadedmetadata)="onMetadataLoaded(livePreview)"></video>
          } @else if (recorderState() === 'reviewing' && capturedImageUrl) {
            <img [src]="capturedImageUrl" alt="Captured Photo" class="video-element object-contain" />
          }
        </div>
      </mat-dialog-content>

      <mat-dialog-actions class="footer mt-0 pt-0!">
        @if (recorderState() === 'previewing') {
          <div class="mx-auto">
            <button
              mat-flat-button
              class="!bg-[#dce1ff] !text-[#3e4759] hover:!bg-[#c5cbff] !rounded-full !h-11 !px-6 flex items-center gap-2 shadow-sm"
              (click)="takePicture()"
              [disabled]="isLoading()"
            >
              <mat-icon>camera_alt</mat-icon>
              <span>{{ 'PhotoRecorder.TakePhoto' | translate }}</span>
            </button>
          </div>
        }
        @if (recorderState() === 'reviewing') {
          <div class="right-actions ml-auto flex items-center gap-2">
            <button matButton (click)="retakePicture()">
              {{ 'PhotoRecorder.Retake' | translate }}
            </button>
            <button mat-button btnType="action" (click)="savePicture()">
              {{ 'PhotoRecorder.Save' | translate }}
            </button>
          </div>
        }
      </mat-dialog-actions>
    </div>
  `,
  styleUrls: ['../../recorders.scss'],
})
export class PhotoRecorderDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('livePreview') livePreviewElement!: ElementRef<HTMLVideoElement>;

  recorderState = signal<PhotoRecorderState>('previewing');
  isLoading = signal(true);
  capturedImageBlob: Blob | null = null;
  capturedImageUrl: SafeUrl | null = null;
  isVerticalCamera = signal(false);

  selectedCameraId = signal<string | null>(null);

  private liveStream: MediaStream | null = null;
  private previewRequestId = 0;
  private sanitizer = inject(DomSanitizer);
  private dialogRef = inject(MatDialogRef<PhotoRecorderDialogComponent>);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private permissionWatcher = inject(MediaDevicePermissionWatcherService);

  ngAfterViewInit(): void {
    const savedCameraId = localStorage.getItem('selectedCamera');
    this.selectedCameraId.set(savedCameraId);
    if (savedCameraId) {
      this.startCameraPreview();
    }
  }

  ngOnDestroy(): void {
    this.stopAllStreams();
  }

  async startCameraPreview(): Promise<void> {
    const requestId = ++this.previewRequestId;
    const cameraAccess = this.permissionWatcher.cameraPermissionState;

    if (cameraAccess === 'denied') {
      this.toast.showDevicePermissionError();
      return;
    }

    if (!this.selectedCameraId()) return;

    this.isLoading.set(true);
    try {
      this.stopAllStreams();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: this.selectedCameraId()! }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });

      if (requestId !== this.previewRequestId) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      this.liveStream = stream;
      this.livePreviewElement.nativeElement.srcObject = this.liveStream;
      this.isLoading.set(false);
    } catch (err: any) {
      console.error('Error accessing media devices:', err);
      this.isLoading.set(false);
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
        this.toast.showDevicePermissionError();
      } else {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'An error occurred', 'OK', { duration: 3000 });
      }
    }
  }

  onMetadataLoaded(video: HTMLVideoElement): void {
    if (video && video.videoWidth && video.videoHeight) {
      const isVertical = video.videoHeight > video.videoWidth;
      this.isVerticalCamera.set(isVertical);
    }
  }

  onCameraSelected(deviceId: string): void {
    this.selectedCameraId.set(deviceId);
    this.startCameraPreview();
  }

  takePicture(): void {
    if (!this.liveStream) return;

    const video = this.livePreviewElement.nativeElement;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context?.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        this.capturedImageBlob = blob;
        this.capturedImageUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
        this.stopAllStreams();
        this.recorderState.set('reviewing');
      }
    }, 'image/png');
  }

  retakePicture(): void {
    this.capturedImageBlob = null;
    this.capturedImageUrl = null;
    this.isVerticalCamera.set(false);
    this.recorderState.set('previewing');
    this.startCameraPreview();
  }

  savePicture(): void {
    this.stopAllStreams();
    this.dialogRef.close(this.capturedImageBlob);
  }

  closeDialog(): void {
    this.stopAllStreams();
    this.dialogRef.close();
  }

  private stopAllStreams(): void {
    if (this.livePreviewElement?.nativeElement?.srcObject) {
      const stream = this.livePreviewElement.nativeElement.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      this.livePreviewElement.nativeElement.srcObject = null;
    }
    if (this.liveStream) {
      this.liveStream.getTracks().forEach((track) => track.stop());
      this.liveStream = null;
    }
  }
}
